import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { TransactionRecordDto } from '../../dtos/transaction-record.dto';
import { TransactionActionEnum } from '../../enums/transaction-action.enum';
import { TransactionStatusEnum } from '../../enums/transaction-status.enum';
import { TransactionTypeEnum } from '../../enums/transaction-type.enum';
import { isPaymentBearingTransactionType } from '../../functions/is-payment-bearing-transaction-type.function';

type TransactionRecordForActionCheck = Pick<
	TransactionRecordDto,
	| 'type'
	| 'paymentId'
	| 'paymentExternalStatus'
	| 'status'
	| 'amount'
	| 'totalGross'
	| 'customerId'
	| 'supplierId'
	| 'links'
>;

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: TransactionRecordForActionCheck,
	action: TransactionActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const statusesAllowedForCancel = [
		TransactionStatusEnum.PENDING,
		TransactionStatusEnum.PROCESSING,
		TransactionStatusEnum.RETRYING,
	];

	switch (action) {
		case TransactionActionEnum.COMPLETE: {
			if (record.paymentId != null) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (
				isPaymentBearingTransactionType(record.type) &&
				record.type !== TransactionTypeEnum.REFUND
			) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			validationResult = setStatusSchema(TransactionStatusEnum.COMPLETED, [
				TransactionStatusEnum.PENDING,
				TransactionStatusEnum.PROCESSING,
			]).validate(record, {
				abortEarly,
			});
			break;
		}

		case TransactionActionEnum.CANCEL: {
			if (
				isPaymentBearingTransactionType(record.type) &&
				record.paymentId != null &&
				record.status === TransactionStatusEnum.COMPLETED
			) {
				return {
					isValid: false,
					errors: ['validations|transaction|cannot-cancel-completed-payment'],
				};
			}
			validationResult = setStatusSchema(
				TransactionStatusEnum.CANCELLED,
				statusesAllowedForCancel,
			).validate(record, {
				abortEarly,
			});
			break;
		}

		case TransactionActionEnum.REFUND: {
			if (record.supplierId != null) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|payment-not-available-for-supplier-transaction',
					],
				};
			}
			if (record.type === TransactionTypeEnum.REFUND) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|refund-cannot-refund-refund-transaction',
					],
				};
			}
			if (
				record.type !== TransactionTypeEnum.RECEIPT &&
				record.type !== TransactionTypeEnum.DEPOSIT
			) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (record.paymentId == null) {
				return {
					isValid: false,
					errors: ['validations|transaction|refund-requires-payment'],
				};
			}
			if (record.status === TransactionStatusEnum.REFUNDED) {
				return {
					isValid: false,
					errors: ['validations|transaction|already-refunded'],
				};
			}
			if (record.status !== TransactionStatusEnum.COMPLETED) {
				return {
					isValid: false,
					errors: ['validations|transaction|refund-requires-completed-payment'],
				};
			}
			validationResult = {
				value: record,
				error: undefined,
			};
			break;
		}

		case TransactionActionEnum.CAPTURE: {
			if (record.supplierId != null) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|payment-not-available-for-supplier-transaction',
					],
				};
			}
			if (!isPaymentBearingTransactionType(record.type)) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (record.paymentId == null) {
				return {
					isValid: false,
					errors: ['validations|transaction|capture-requires-payment'],
				};
			}
			if (record.status !== TransactionStatusEnum.PROCESSING) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (record.paymentExternalStatus !== 'AUTHORIZED') {
				return {
					isValid: false,
					errors: [
						'validations|transaction|capture-requires-authorized-status',
					],
				};
			}
			validationResult = {
				value: record,
				error: undefined,
			};
			break;
		}

		case TransactionActionEnum.SEND_PAYMENT_LINK: {
			if (record.supplierId != null) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|payment-not-available-for-supplier-transaction',
					],
				};
			}
			if (
				record.type !== TransactionTypeEnum.RECEIPT &&
				record.type !== TransactionTypeEnum.DEPOSIT
			) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (record.status !== TransactionStatusEnum.PENDING) {
				return {
					isValid: false,
					errors: ['validations|transaction|action-not-applicable-for-type'],
				};
			}
			if (record.paymentId != null) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|send-payment-link-requires-no-payment',
					],
				};
			}
			const amount = Number(record.amount ?? record.totalGross ?? 0);
			if (isNaN(amount) || amount <= 0) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|send-payment-link-requires-positive-amount',
					],
				};
			}
			const customerId = record.customerId ?? record.links?.customerId ?? null;

			if (customerId == null) {
				return {
					isValid: false,
					errors: [
						'validations|transaction|payment-request-requires-linked-customer',
					],
				};
			}
			validationResult = {
				value: record,
				error: undefined,
			};
			break;
		}

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
